import _defineProperty from "@babel/runtime/helpers/defineProperty";
/**@jsx jsx */
import { jsx } from '@emotion/react';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { IntlProvider, injectIntl } from 'react-intl-next';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import { isFileIdentifier, isProcessedFileState } from '@atlaskit/media-client';
import { withMediaClient } from '@atlaskit/media-client-react';
import { MediaViewer } from '@atlaskit/media-viewer';
import { mediaTableWrapperStyles } from './styles';
import DownloadButton from './downloadButton';
import PreviewButton from './previewButton';
import { generateRowValues, getValidTableProps, generateHeadValues, CELL_KEY_DOWNLOAD, CELL_KEY_PREVIEW, ANALYTICS_MEDIA_CHANNEL } from '../util';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
export class MediaTable extends Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      fileInfoState: new Map()
    });
    _defineProperty(this, "subscriptions", []);
    _defineProperty(this, "hasBeenMounted", false);
    _defineProperty(this, "unsubscribe", () => {
      this.subscriptions.forEach(subscription => subscription.unsubscribe());
      this.subscriptions = [];
    });
    _defineProperty(this, "generateCellValues", (data, identifier) => {
      const cellValues = [];
      const {
        columns
      } = this.props;
      columns.cells.forEach(cell => {
        const content = cell.key === CELL_KEY_DOWNLOAD ? jsx(DownloadButton, {
          onClick: this.onDownloadClick(identifier)
        }) : cell.key === CELL_KEY_PREVIEW ? jsx(PreviewButton, {
          onClick: this.onPreviewClick(identifier)
        }) : cell.key && data[cell.key] || '';
        cellValues.push({
          key: cell.key,
          content
        });
      });
      return cellValues;
    });
    _defineProperty(this, "onDownloadClick", identifier => event => {
      const {
        id,
        collectionName
      } = identifier;
      event.stopPropagation();
      const {
        mediaClient
      } = this.props;
      const {
        fileInfoState
      } = this.state;
      const nameInfo = fileInfoState.get(id);
      mediaClient.file.downloadBinary(id, nameInfo ? nameInfo.fileName : '', collectionName);
    });
    _defineProperty(this, "onPreviewClick", identifier => event => {
      event.stopPropagation();
      this.openPreview(identifier);
    });
    _defineProperty(this, "onSort", data => {
      const {
        onSort
      } = this.props;
      const {
        key,
        sortOrder,
        item
      } = data;
      item && item.isSortable && onSort && onSort(key, sortOrder);
    });
    _defineProperty(this, "renderRowValues", (validItemsPerPage, validPageNumber, validTotalItems) => {
      const {
        items,
        columns
      } = this.props;
      const rowValues = items.map((item, index) => {
        const {
          data,
          identifier,
          rowProps
        } = item;
        return {
          cells: this.generateCellValues(data, identifier),
          key: identifier.id,
          tabIndex: 0,
          onClick: this.onRowClick(identifier, data, index),
          onKeyPress: event => {
            if (event.key === 'Enter') {
              this.onRowEnterKeyPressed(identifier, data, index);
            }
          },
          ...rowProps
        };
      });
      return generateRowValues({
        itemsPerPage: validItemsPerPage,
        pageNumber: validPageNumber,
        totalItems: validTotalItems,
        rowValues,
        headerCells: columns.cells
      });
    });
    _defineProperty(this, "renderTable", () => {
      const {
        columns,
        itemsPerPage,
        isLoading,
        pageNumber,
        onSetPage,
        totalItems,
        items,
        sortKey,
        sortOrder,
        highlightedRowIndex
      } = this.props;
      const {
        validItemsPerPage,
        validPageNumber,
        validTotalItems
      } = getValidTableProps(items.length, itemsPerPage, pageNumber, totalItems);
      const rowsPerPage = validTotalItems <= validItemsPerPage ? undefined : validItemsPerPage;
      return jsx(DynamicTableStateless, {
        caption: '',
        head: generateHeadValues(columns),
        rows: this.renderRowValues(validItemsPerPage, validPageNumber, validTotalItems),
        rowsPerPage: rowsPerPage,
        page: validPageNumber,
        loadingSpinnerSize: "large",
        isLoading: isLoading,
        sortKey: sortKey,
        sortOrder: sortOrder,
        onSort: this.onSort,
        onSetPage: onSetPage,
        isFixedSize: true,
        highlightedRowIndex: highlightedRowIndex
      });
    });
    _defineProperty(this, "openPreview", identifier => {
      const {
        onPreviewOpen
      } = this.props;
      this.safeSetState({
        mediaViewerSelectedItem: identifier
      });
      onPreviewOpen && onPreviewOpen();
    });
    _defineProperty(this, "onRowEnterKeyPressed", (identifier, data, index) => {
      const {
        createAnalyticsEvent,
        onRowClick
      } = this.props;
      const ev = createAnalyticsEvent({
        eventType: 'ui',
        action: 'keyPressed',
        actionSubject: 'mediaFile',
        actionSubjectId: 'mediaFileRow'
      });
      ev.fire(ANALYTICS_MEDIA_CHANNEL);
      const shouldPreventDefaultRowClick = onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(data, index);
      if (!shouldPreventDefaultRowClick) {
        this.openPreview(identifier);
      }
    });
    _defineProperty(this, "onRowClick", (identifier, data, index) => () => {
      const {
        createAnalyticsEvent,
        onRowClick
      } = this.props;
      const ev = createAnalyticsEvent({
        eventType: 'ui',
        action: 'clicked',
        actionSubject: 'mediaFile',
        actionSubjectId: 'mediaFileRow'
      });
      ev.fire(ANALYTICS_MEDIA_CHANNEL);
      const shouldPreventDefaultRowClick = onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(data, index);
      if (!shouldPreventDefaultRowClick) {
        this.openPreview(identifier);
      }
    });
    _defineProperty(this, "safeSetState", state => {
      if (this.hasBeenMounted) {
        this.setState(state);
      }
    });
    _defineProperty(this, "onMediaViewerClose", () => {
      const {
        onPreviewClose
      } = this.props;
      this.safeSetState({
        mediaViewerSelectedItem: undefined
      });
      onPreviewClose && onPreviewClose();
    });
    _defineProperty(this, "renderMediaViewer", () => {
      const {
        mediaViewerSelectedItem
      } = this.state;
      const {
        mediaClient,
        items
      } = this.props;
      if (!mediaViewerSelectedItem) {
        return null;
      }
      const mediaViewerItems = items.map(item => item.identifier);
      const collectionName = isFileIdentifier(mediaViewerSelectedItem) && mediaViewerSelectedItem.collectionName || '';
      return /*#__PURE__*/ReactDOM.createPortal(jsx(MediaViewer, {
        mediaClientConfig: mediaClient.config,
        items: mediaViewerItems,
        selectedItem: mediaViewerSelectedItem,
        onClose: this.onMediaViewerClose,
        collectionName: collectionName
      }), document.body);
    });
  }
  componentDidMount() {
    this.hasBeenMounted = true;
    const {
      items,
      mediaClient
    } = this.props;
    items.forEach(async item => {
      const {
        identifier: {
          id,
          collectionName = ''
        }
      } = item;
      const subscription = mediaClient.file.getFileState(id, {
        collectionName
      }).subscribe({
        next: fileState => {
          const {
            fileInfoState
          } = this.state;
          if (isProcessedFileState(fileState)) {
            fileInfoState.set(id, {
              fileName: fileState.name,
              id
            });
            if (this.hasBeenMounted) {
              this.safeSetState({
                fileInfoState
              });
            }
          }
        }
      });
      this.subscriptions.push(subscription);
    });
  }
  componentWillUnmount() {
    this.hasBeenMounted = false;
    this.unsubscribe();
  }
  render() {
    const content = jsx("div", {
      css: mediaTableWrapperStyles
    }, this.renderTable(), this.renderMediaViewer());
    return this.props.intl ? content : jsx(IntlProvider, {
      locale: "en"
    }, content);
  }
}
export default withMediaClient(withAnalyticsEvents()(injectIntl(MediaTable, {
  enforceContext: false
})));